/**
 * ApiService Base - Clase base para el servicio API
 * Proporciona funcionalidad común: tokens, cliente HTTP, baseUrl
 */

import { TokenManager } from './tokenManager';
import { ApiClient, type ApiClientConfig } from './ApiClient';
import type { User } from '../types/auth';
import logger from '../../../utils/simpleLogger';

export class ApiServiceBase {
  protected baseUrl: string;
  protected tokenManager: TokenManager;
  protected client: ApiClient;

  constructor() {
    const envBaseUrl = import.meta.env.VITE_API_BASE_URL;
    const defaultBaseUrl = import.meta.env.DEV
      ? 'http://localhost:8001/api'
      : '/api';

    // Normalizar para evitar doble barra final
    const normalizedBaseUrl = (envBaseUrl || defaultBaseUrl).replace(/\/$/, '');

    this.baseUrl = normalizedBaseUrl;
    // Usar instancia singleton para compartir tokens entre todos los servicios
    this.tokenManager = TokenManager.getInstance();

    // Configurar ApiClient con callbacks para tokens
    const clientConfig: ApiClientConfig = {
      baseUrl: this.baseUrl,
      getToken: () => this.tokenManager.getAccessToken(),
      getRefreshToken: () => this.tokenManager.getRefreshToken(),
      refreshAccessToken: () => this.refreshAccessToken(),
      clearTokens: () => this.tokenManager.clearTokensFromStorage(),
    };

    this.client = new ApiClient(clientConfig);
  }

  /**
   * Método protegido para hacer peticiones HTTP
   * Los servicios hijos pueden usar este método
   */
  protected async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.client.request<T>(endpoint, options);
  }

  /**
   * Refresca el token de acceso usando el refresh token
   */
  protected async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.tokenManager.getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${refreshToken}`
        },
      });

      const data = await response.json();

      if (data.success && data.access_token) {
        this.tokenManager.updateAccessToken(data.access_token, data.user);
        return true;
      }

      return false;
    } catch (error) {
      logger.error('❌ [API] Token refresh failed', error);
      return false;
    }
  }

  /**
   * Obtiene el usuario actual
   */
  getCurrentUser(): User | null {
    return this.tokenManager.getCurrentUser();
  }

  /**
   * Obtiene el baseUrl (útil para servicios hijos)
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }
}
